import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import syncService from '../services/syncService';

/**
 * JoinCollaboration component
 * Handles invite token redemption and redirects to project
 */
export default function JoinCollaboration() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading } = useAuth();
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);
  const [projectInfo, setProjectInfo] = useState(null);

  useEffect(() => {
    const joinProject = async () => {
      // Wait for auth to load
      if (isLoading) {
        return;
      }

      // Check if collaboration feature is enabled
      const enableCollaboration = localStorage.getItem('enableCollaboration') === 'true';
      
      if (!enableCollaboration) {
        setStatus('error');
        setError('Collaboration feature is not enabled. Please enable it in Settings > Advanced.');
        return;
      }

      // Check if user is authenticated
      if (!isAuthenticated || !user) {
        // Store the token and redirect to login
        sessionStorage.setItem('pendingInviteToken', token);
        navigate('/login');
        return;
      }

      try {
        setStatus('loading');
        
        // Get user info
        const userId = user.id || user.sub || user.email;
        const username = user.name || user.email || 'Anonymous';
        
        // Join project via token (pass userId and username)
        const result = await syncService.joinByToken(token, userId, username);
        
        if (!result.success) {
          throw new Error(result.error || 'Failed to join project');
        }

        const { projectId, projectName } = result.data;
        setProjectInfo({ projectId, projectName });

        // Initialize sync service for this project
        await syncService.initialize(userId, projectId, username);

        setStatus('success');
        
        // Redirect to project immediately
        navigate(`/?projectId=${projectId}`);

      } catch (err) {
        console.error('[JoinCollaboration] Error:', err);
        setStatus('error');
        setError(err.message || 'Failed to join project');
      }
    };

    if (token) {
      joinProject();
    } else {
      setStatus('error');
      setError('Invalid invite link');
    }
  }, [token, user, isAuthenticated, isLoading, navigate]);

  // Check for pending invite token after login
  useEffect(() => {
    const pendingToken = sessionStorage.getItem('pendingInviteToken');
    if (pendingToken && isAuthenticated && token === pendingToken) {
      sessionStorage.removeItem('pendingInviteToken');
      // Token will be processed by the main useEffect
    }
  }, [isAuthenticated, token]);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      padding: '20px',
      backgroundColor: '#1e1e1e',
      color: '#ffffff',
    }}>
      <div style={{
        maxWidth: '500px',
        textAlign: 'center',
        padding: '40px',
        backgroundColor: '#2d2d2d',
        borderRadius: '8px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
      }}>
        {status === 'loading' && (
          <>
            <div style={{
              width: '50px',
              height: '50px',
              border: '4px solid #404040',
              borderTop: '4px solid #007acc',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 20px',
            }} />
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
            <h2 style={{ marginBottom: '10px' }}>Joining Project...</h2>
            <p style={{ color: '#999' }}>
              Please wait while we add you to the collaboration session
            </p>
          </>
        )}

        {status === 'success' && projectInfo && (
          <>
            <div style={{
              width: '50px',
              height: '50px',
              borderRadius: '50%',
              backgroundColor: '#28a745',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              fontSize: '30px',
            }}>
              ✓
            </div>
            <h2 style={{ marginBottom: '10px', color: '#28a745' }}>Success!</h2>
            <p style={{ color: '#ccc', marginBottom: '20px' }}>
              You've joined <strong>{projectInfo.projectName}</strong>
            </p>
            <p style={{ color: '#999', fontSize: '14px' }}>
              Redirecting to project...
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div style={{
              width: '50px',
              height: '50px',
              borderRadius: '50%',
              backgroundColor: '#dc3545',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              fontSize: '30px',
            }}>
              ✕
            </div>
            <h2 style={{ marginBottom: '10px', color: '#dc3545' }}>Error</h2>
            <p style={{ color: '#ccc', marginBottom: '20px' }}>
              {error}
            </p>
            <button
              onClick={() => navigate('/')}
              style={{
                padding: '10px 20px',
                backgroundColor: '#007acc',
                color: '#ffffff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#005999'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#007acc'}
            >
              Go to Home
            </button>
          </>
        )}
      </div>
    </div>
  );
}
